import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useDevMode } from '@/contexts/DevModeContext';

export interface Notification {
  id: string;
  user_id: string;
  type: string;
  title: string;
  message: string | null;
  image_url: string | null;
  link: string | null;
  actor_id: string | null;
  poll_id: string | null;
  is_read: boolean;
  created_at: string;
  actor_profile?: {
    display_name: string | null;
    avatar_url: string | null;
  } | null;
}

export function useNotifications() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { isDevMode } = useDevMode();
  const [unreadCount, setUnreadCount] = useState(0);

  const {
    data: notifications = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['notifications', user?.id],
    queryFn: async () => {
      if (!user) return [];

      // Fetch notifications
      const { data: notifs, error: notifsError } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (notifsError) throw notifsError;
      if (!notifs || notifs.length === 0) return [];

      // Get unique actor IDs
      const actorIds = [...new Set(notifs.map(n => n.actor_id).filter(Boolean))] as string[];

      // Fetch actor profiles
      let profileMap = new Map<string, { display_name: string | null; avatar_url: string | null }>();
      if (actorIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, display_name, avatar_url')
          .in('user_id', actorIds);

        if (profiles) {
          profiles.forEach(p => {
            profileMap.set(p.user_id, { display_name: p.display_name, avatar_url: p.avatar_url });
          });
        }
      }

      // Combine notifications with actor profiles
      return notifs.map(n => ({
        ...n,
        actor_profile: n.actor_id ? profileMap.get(n.actor_id) || null : null,
      })) as Notification[];
    },
    enabled: !!user,
  });

  // Calculate unread count
  useEffect(() => {
    const count = notifications.filter(n => !n.is_read).length;
    setUnreadCount(count);
  }, [notifications]);

  // Subscribe to realtime notifications - update cache directly instead of refetching
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('notifications-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          // Update cache directly with new notification instead of full refetch
          const newNotification = payload.new as Notification;
          queryClient.setQueryData<Notification[]>(
            ['notifications', user.id],
            (old = []) => {
              // Avoid duplicates
              if (old.some(n => n.id === newNotification.id)) return old;
              // Add to beginning (most recent first)
              return [{ ...newNotification, actor_profile: null }, ...old];
            }
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);

  // Mark notification as read
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', user?.id] });
    },
  });

  // Mark all as read
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      if (!user) return;

      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', user?.id] });
    },
  });

  // Delete notification
  const deleteNotificationMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', user?.id] });
    },
  });

  return {
    notifications: isDevMode ? [] : notifications,
    isLoading,
    error,
    unreadCount: isDevMode ? 0 : unreadCount,
    markAsRead: markAsReadMutation.mutate,
    markAllAsRead: markAllAsReadMutation.mutate,
    deleteNotification: deleteNotificationMutation.mutate,
    refetch,
  };
}
